import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, DollarSign, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FLAG_ORDER = { overpriced: 0, below_benchmark: 1, in_range: 2, no_benchmark: 3, unmatched: 4 };

const FlagChip = ({ flag }) => {
  const styles = {
    overpriced: 'bg-[#FF3B30] text-white border-black',
    in_range: 'bg-zinc-200 text-zinc-800 border-black',
    below_benchmark: 'bg-[#34C759] text-white border-black',
    unmatched: 'bg-transparent text-zinc-500 border-zinc-400',
    no_benchmark: 'bg-transparent text-zinc-500 border-zinc-400',
  };
  const labels = {
    overpriced: 'OVERPRICED',
    in_range: 'IN RANGE',
    below_benchmark: 'BELOW',
    unmatched: 'NOT MATCHED',
    no_benchmark: 'NO BENCHMARK YET',
  };
  return (
    <span
      data-testid={`price-check-flag-${flag}`}
      className={`inline-block px-1.5 py-0.5 text-[10px] font-heading font-semibold tracking-wider border whitespace-nowrap ${styles[flag]}`}
    >
      {labels[flag]}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  if (!source) return <span className="text-zinc-400">—</span>;
  const labels = { seed: 'REF', observed: 'PEER DATA', peer: 'PEER' };
  return (
    <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-zinc-100 text-zinc-600 border border-zinc-300 whitespace-nowrap">
      {labels[source] || source.toUpperCase()}
    </span>
  );
};

const confidenceLabel = (c) => (c < 0.5 ? 'Low' : c <= 0.75 ? 'Medium' : 'High');

const PriceCheckPanel = ({ documentId, vendorName, lineItems }) => {
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/price-intel/analysis/${documentId}`, { withCredentials: true })
      .then((res) => { if (!cancelled && res.data?.analysis) setAnalysis(res.data.analysis); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [documentId]);

  const runCheck = async () => {
    setLoading(true);
    try {
      const payload = {
        invoice_id: documentId,
        contractor_id: user?.company_id || 'unknown',
        vendor_name: vendorName || null,
        line_items: lineItems
          .filter((li) => (li.product_description || '').trim())
          .map((li) => ({
            raw_description: li.product_description,
            quantity: parseFloat(li.quantity) || 0,
            raw_uom: li.unit || null,
            unit_price: parseFloat(li.unit_price) || 0,
            extended_price: parseFloat(li.extended_price) || null,
          })),
      };
      const res = await axios.post(`${API}/price-intel/analyze`, payload, { withCredentials: true });
      setAnalysis(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Price check failed');
    } finally {
      setLoading(false);
    }
  };

  const hasOverpay = analysis && analysis.total_overpay > 0;
  const sortedLines = analysis
    ? [...analysis.lines].sort((a, b) => (FLAG_ORDER[a.flag] ?? 9) - (FLAG_ORDER[b.flag] ?? 9))
    : [];

  return (
    <div className="mb-6" data-testid="price-check-panel">
      {!analysis && (
        <button
          onClick={runCheck}
          disabled={loading || lineItems.length === 0}
          data-testid="run-price-check-btn"
          className="w-full h-14 bg-[#111111] text-white font-heading font-bold uppercase tracking-wider text-sm border-2 border-black shadow-[4px_4px_0_0_#FFC40C] hover:shadow-[2px_2px_0_0_#FFC40C] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60 flex items-center justify-center gap-3"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin text-[#FFC40C]" />
              Checking prices...
            </>
          ) : (
            <>
              <DollarSign className="w-5 h-5 text-[#FFC40C]" />
              Run Price Check
            </>
          )}
        </button>
      )}

      {analysis && (
        <>
          {/* HERO INSIGHT CARD */}
          <div
            data-testid="price-check-hero"
            className="relative bg-[#141414] rounded-lg border-2 border-black shadow-[6px_6px_0_0_#111111] overflow-hidden mb-2"
          >
            <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#FFC40C]" />
            <div className="p-5 sm:p-7 pl-6 sm:pl-9">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <p className="text-[#FFC40C] font-heading font-semibold uppercase tracking-[0.2em] text-[11px]">
                  Punchlist &middot; Price Check
                </p>
                <span
                  data-testid="price-check-confidence-pill"
                  className="px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider bg-white/10 text-zinc-300 border border-zinc-600 rounded-full whitespace-nowrap"
                >
                  {confidenceLabel(analysis.analysis_confidence)} confidence
                </span>
              </div>
              {hasOverpay ? (
                <h2 className="text-white font-heading font-bold text-2xl sm:text-4xl leading-tight" data-testid="price-check-headline">
                  You're overpaying{' '}
                  <span className="text-[#FFC40C] font-mono">${fmt(analysis.total_overpay)}</span>{' '}
                  on this order.
                </h2>
              ) : (
                <h2 className="text-white font-heading font-bold text-2xl sm:text-3xl leading-tight" data-testid="price-check-headline">
                  No overpayment detected on matched lines.
                </h2>
              )}
              <p className="text-zinc-400 text-sm sm:text-base mt-3" data-testid="price-check-subline">
                You paid <span className="font-mono text-zinc-200">${fmt(analysis.total_paid)}</span>.
                {' '}Contractors like you pay <span className="font-mono text-zinc-200">${fmt(analysis.total_benchmark)}</span>.
              </p>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-4 px-1" data-testid="price-check-caption">
            Price intelligence is in active development. Benchmarks strengthen as more contractor orders are analyzed.
          </p>

          {/* LINE-ITEM TABLE */}
          <div className="bg-white border-2 border-black shadow-[4px_4px_0_0_#111111] overflow-x-auto" data-testid="price-check-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-[#111111] text-white font-heading uppercase tracking-wider text-[11px]">
                  <th className="text-left px-3 py-2.5">Item</th>
                  <th className="text-right px-3 py-2.5">Qty</th>
                  <th className="text-right px-3 py-2.5">You Paid</th>
                  <th className="text-right px-3 py-2.5">Benchmark</th>
                  <th className="text-left px-3 py-2.5">Source</th>
                  <th className="text-left px-3 py-2.5">Flag</th>
                  <th className="text-right px-3 py-2.5">Overpaid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {sortedLines.map((ln, i) => (
                  <tr key={`${ln.raw_description}_${i}`} data-testid={`price-check-row-${i}`} className={ln.flag === 'overpriced' ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2.5 font-medium max-w-[240px]">
                      <span className={ln.flag === 'unmatched' ? 'text-zinc-500' : ''}>
                        {ln.flag === 'unmatched' ? ln.raw_description : (ln.matched_name || ln.raw_description)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{ln.quantity}</td>
                    <td className="px-3 py-2.5 text-right font-mono">${fmt(ln.unit_price)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {ln.benchmark_unit_price != null ? `$${fmt(ln.benchmark_unit_price)}` : '—'}
                    </td>
                    <td className="px-3 py-2.5"><SourceBadge source={ln.benchmark_source} /></td>
                    <td className="px-3 py-2.5"><FlagChip flag={ln.flag} /></td>
                    <td className={`px-3 py-2.5 text-right font-mono font-semibold ${ln.flag === 'overpriced' ? 'text-[#FF3B30]' : 'text-zinc-400'}`}>
                      {ln.flag === 'overpriced' ? `$${fmt(ln.overpay_amount)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={runCheck}
            disabled={loading}
            data-testid="rerun-price-check-btn"
            className="mt-3 h-10 px-4 bg-white text-black font-heading font-bold uppercase tracking-wider text-xs border-2 border-black shadow-[3px_3px_0_0_#111111] hover:shadow-[1px_1px_0_0_#111111] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-run Price Check
          </button>
        </>
      )}
    </div>
  );
};

export default PriceCheckPanel;
